import { useState, useEffect } from 'react';
import { makeRequest2 } from '../../../utils/makeRequest';
import { toastify } from '../../../utils/toast';
import { addEmailSettings, getEmailSettings } from '../../../utils/urls';
import { Button } from 'primereact/button';

const EmailTab = ({ facilityId }) => {
    const [emailSettings, setEmailSettings] = useState({
        host: '',
        port: '',
        secure: false,
        sender: '',
        senderName: '',
        user: '',
        pass: '',
        rejectUnauthorized: false
    });
    const [emailList, setEmailList] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [formDisabled, setFormDisabled] = useState(false);

    useEffect(() => {
        if (facilityId) {
            console.log('Component mounted with facilityId:', facilityId);
            fetchEmailSettings();
        } else {
            console.log('No facilityId provided');
        }
    }, [facilityId]);

    const fetchEmailSettings = async () => {
        try {
            console.log('Fetching email settings from:', `${getEmailSettings}/${facilityId}`);
            const response = await makeRequest2(`${getEmailSettings}/${facilityId}`, 'GET');

            console.log('Full response:', response);

            // Handle the nested data structure: response.data.data
            if (response && response.data && response.data.success) {
                if (response.data.data && response.data.data !== null) {
                    console.log('Email settings data found:', response.data.data);
                    const data = [response.data.data];
                    setEmailList(data);
                    setFormDisabled(true);
                    console.log('Form disabled because settings exist');
                } else {
                    console.log('No email settings found (data.data is null), enabling form');
                    setEmailList([]);
                    setFormDisabled(false);
                }
            } else {
                console.log('API call failed or no success flag, enabling form');
                setEmailList([]);
                setFormDisabled(false);
            }
        } catch (error) {
            console.error('Error fetching email settings:', error);
            setEmailList([]);
            setFormDisabled(false);

            // Only show error toast for actual errors, not when no data exists
            if (!error.message?.includes('404') && !error.message?.includes('not found')) {
                toastify('Failed to fetch email settings', 'error');
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const url = `${addEmailSettings}/${facilityId}${isEditing ? `/${emailSettings._id}` : ''}`;
            const method = isEditing ? 'PUT' : 'POST';

            // Ensure proper data types
            const payload = {
                ...emailSettings,
                port: parseInt(emailSettings.port) || 587, // Ensure port is a number
                secure: Boolean(emailSettings.secure),
                rejectUnauthorized: Boolean(emailSettings.rejectUnauthorized)
            };

            console.log('Submitting email settings to:', url, 'Method:', method);
            console.log('Payload:', payload);

            const response = await makeRequest2(url, method, payload);

            console.log('Response received:', response);

            if (response && response.data && response.data.success) {
                toastify(`Email settings ${isEditing ? 'updated' : 'added'} successfully`, 'success');
                fetchEmailSettings();
                resetForm();
            } else if (response && (response.success || response.message)) {
                toastify(`Email settings ${isEditing ? 'updated' : 'added'} successfully`, 'success');
                fetchEmailSettings();
                resetForm();
            } else {
                console.error('Unexpected response format:', response);
                toastify(response?.data?.error || response?.message || 'Operation failed', 'error');
            }
        } catch (error) {
            console.error('Error saving email settings:', error);
            console.error('Error details:', error.response?.data || error.message);
            toastify(error.response?.data?.error || 'Error saving email settings', 'error');
        }
    };

    const handleEdit = (email) => {
        setEmailSettings({
            ...email,
            pass: '' // Don't populate password field for security
        });
        setIsEditing(true);
        setFormDisabled(false);
    };

    const resetForm = () => {
        setEmailSettings({
            host: '',
            port: '',
            secure: false,
            sender: '',
            senderName: '',
            user: '',
            pass: '',
            rejectUnauthorized: false
        });
        setIsEditing(false);
        setFormDisabled(emailList.length > 0);
    };

    return (
        <div className="row p-3">
            <div className="col-lg-6">
                <div className="card">
                    <div className="card-body">
                        <h5>{isEditing ? 'Update' : 'Add'} Email Settings</h5>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group mt-3">
                                <label>Host*</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={emailSettings.host}
                                    onChange={(e) => setEmailSettings({ ...emailSettings, host: e.target.value })}
                                    required
                                    disabled={formDisabled && !isEditing}
                                    placeholder="e.g., smtp.gmail.com"
                                />
                            </div>

                            <div className="form-group mt-3">
                                <label>Port*</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={emailSettings.port}
                                    onChange={(e) => setEmailSettings({ ...emailSettings, port: parseInt(e.target.value) || '' })}
                                    required
                                    disabled={formDisabled && !isEditing}
                                    placeholder="e.g., 587, 465, 25"
                                />
                            </div>

                            <div className="form-group mt-3">
                                <div className="form-check">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="secure"
                                        checked={emailSettings.secure}
                                        onChange={(e) => setEmailSettings({ ...emailSettings, secure: e.target.checked })}
                                        disabled={formDisabled && !isEditing}
                                    />
                                    <label className="form-check-label" htmlFor="secure">
                                        Use SSL/TLS (Secure Connection)
                                    </label>
                                </div>
                            </div>

                            <div className="form-group mt-3">
                                <label>Sender Email*</label>
                                <input
                                    type="email"
                                    className="form-control"
                                    value={emailSettings.sender}
                                    onChange={(e) => setEmailSettings({ ...emailSettings, sender: e.target.value })}
                                    required
                                    disabled={formDisabled && !isEditing}
                                    placeholder="sender@example.com"
                                />
                            </div>
                            <div className="form-group mt-3">
                                <label>Sender Name*</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={emailSettings.senderName}
                                    onChange={(e) => setEmailSettings({ ...emailSettings, senderName: e.target.value })}
                                    required
                                    disabled={formDisabled && !isEditing}
                                    placeholder="MYNAME"
                                />
                            </div>

                            <div className="form-group mt-3">
                                <label>Email Username*</label>
                                <input
                                    type="email"
                                    className="form-control"
                                    value={emailSettings.user}
                                    onChange={(e) => setEmailSettings({ ...emailSettings, user: e.target.value })}
                                    required
                                    disabled={formDisabled && !isEditing}
                                    placeholder="your-email@example.com"
                                />
                            </div>

                            <div className="form-group mt-3">
                                <label>Email Password*</label>
                                <input
                                    type="password"
                                    className="form-control"
                                    value={emailSettings.pass}
                                    onChange={(e) => setEmailSettings({ ...emailSettings, pass: e.target.value })}
                                    required={!isEditing}
                                    disabled={formDisabled && !isEditing}
                                    placeholder={isEditing ? "Enter new password or leave blank to keep current" : ""}
                                />
                            </div>

                            <div className="form-group mt-3">
                                <div className="form-check">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="rejectUnauthorized"
                                        checked={emailSettings.rejectUnauthorized}
                                        onChange={(e) => setEmailSettings({ ...emailSettings, rejectUnauthorized: e.target.checked })}
                                        disabled={formDisabled && !isEditing}
                                    />
                                    <label className="form-check-label" htmlFor="rejectUnauthorized">
                                        Reject Unauthorized Connections
                                    </label>
                                </div>
                            </div>

                            <div className="mt-4 text-end">
                                {isEditing && (
                                    <button type="button" className="btn btn-secondary me-2" onClick={resetForm}>
                                        Cancel
                                    </button>
                                )}
                                {(!formDisabled || isEditing) && (
                                    <button type="submit" className="btn btn-primary">
                                        {isEditing ? 'Update' : 'Add'} Email Settings
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <div className="col-lg-6">
                <div className="card">
                    <div className="card-body">
                        <h5>Current Email Settings</h5>
                        <div className="table-responsive">
                            <table className="table align-middle">
                                <thead>
                                    <tr>
                                        <th>HOST</th>
                                        <th>PORT</th>
                                        <th>SENDER</th>
                                        <th>SENDERNAME</th>
                                        <th>USER</th>
                                        <th>SECURE</th>
                                        <th className="text-end">ACTIONS</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {emailList.length > 0 ? (
                                        emailList.map((email, index) => (
                                            <tr key={email._id || index}>
                                                <td>{email.host || 'N/A'}</td>
                                                <td>{email.port || 'N/A'}</td>
                                                <td>{email.sender || 'N/A'}</td>
                                                <td>{email.senderName || 'N/A'}</td>
                                                <td>{email.user || 'N/A'}</td>
                                                <td>
                                                    <span className={`badge ${email.secure ? 'bg-success' : 'bg-secondary'}`}>
                                                        {email.secure ? 'Yes' : 'No'}
                                                    </span>
                                                </td>
                                                <td className="text-end">
                                                    <Button
                                                        icon="ti ti-pencil"
                                                        className="p-button-rounded p-button-warning me-2"
                                                        onClick={() => handleEdit(email)}
                                                        tooltip="Edit"
                                                    />
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="text-center py-4">
                                                <div className="text-muted">
                                                    <i className="ti ti-inbox mb-2" style={{ fontSize: "24px" }}></i>
                                                    <p className="mb-0">No email settings available</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmailTab;